import 'react-app-polyfill/stable';
import 'react-app-polyfill/ie11';
import 'locales/i18n';
import 'moment/locale/vi';
import 'utils/datetime';
import './firebase';
import 'swagger-ui-react/swagger-ui.css';

import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { Persister } from '@tanstack/react-query-persist-client';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ConfigProvider } from 'antd';
import viVN from 'antd/lib/locale/vi_VN';
import App from 'App';
import FontFaceObserver from 'fontfaceobserver';
import ErrorBoundary from 'pages/ErrorBoundary';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import reportWebVitals from 'reportWebVitals';
import { createIDBPersister } from 'services/persistQueryClient';
import { configureAppStore } from 'store';
import { form, theme } from 'styles';

// Observe loading of Inter (to remove 'Inter', remove the <link> tag in
// the index.html file and this observer)
const openSansObserver = new FontFaceObserver('Inter', {});

// When Inter is loaded, add a font-family using Inter to the body
openSansObserver.load().then(() => {
  document.body.classList.add('fontLoaded');
});

const store = configureAppStore();
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

const persister = createIDBPersister() as Persister;

root.render(
  <Provider store={store}>
    <React.StrictMode>
      <ErrorBoundary>
        <ConfigProvider locale={viVN} form={form} theme={theme}>
          <PersistQueryClientProvider
            client={queryClient}
            persistOptions={{
              persister,
              maxAge: Infinity,
              buster: '1',
            }}
          >
            <App />
            <ReactQueryDevtools position="bottom-right" />
          </PersistQueryClientProvider>
        </ConfigProvider>
      </ErrorBoundary>
    </React.StrictMode>
  </Provider>
);

// Hot reloadable translation json files
if (module.hot) {
  module.hot.accept(['./locales/i18n'], () => {
    // No need to render the App again because i18next works with the hooks
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
